import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { TableColumn } from '@swimlane/ngx-datatable';
import { distinctUntilChanged, finalize, take } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { TeachCompensationService } from 'src/app/services/api/teach-compensation/teach-compensation.service';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import {
  CompensationSummaryErpApprovedRequest,
  CompensationSummaryErpComparisonDetail,
  CompensationSummaryErpComparisonTable,
  CompensationSummaryErpComparisonTableRow,
  CompensationSummaryLineItem,
  CompensationSummaryErpRowResult,
  CompensationSummaryParams,
  CompensationSummaryInternationalWeeklyWorkload,
  CompensationSummaryInternationalWeeklyWorkloadItem,
  CompensationSummaryRegularLevel,
  CompensationSummaryRegularWeeklyWorkload,
  CompensationSummaryRegularWeeklyWorkloadItem,
  CompensationSummaryResponse,
} from 'src/app/services/models/teach-compensation/teach-compensation.model';
import {
  createDatatableSortParams,
  Datatable,
  DatatableSortChangeEvent,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';
import { SharedModule } from 'src/shared/shared.module';

type CompensationSummaryComparisonStatus = 'matched' | 'missing_erp' | 'not_approved' | 'missing_approved_request';
type CompensationSummaryTab = 'requests' | 'compensation';
type CompensationCourseSummaryTab = 'regular' | 'international';
type CompensationCourseSummaryRowType = 'data' | 'total';

const THAI_WEEKDAY_NAMES: Record<number, string> = {
  0: 'วันอาทิตย์',
  1: 'วันจันทร์',
  2: 'วันอังคาร',
  3: 'วันพุธ',
  4: 'วันพฤหัสบดี',
  5: 'วันศุกร์',
  6: 'วันเสาร์',
  7: 'วันอาทิตย์',
};

interface CompensationSummaryComparisonRow {
  id: string;
  sequence: number;
  systemRequest: CompensationSummaryErpApprovedRequest | null;
  erpRequest: CompensationSummaryErpRowResult | null;
  officialTripRequest: CompensationSummaryErpRowResult | null;
  leaveDate: string | null;
  comparisonStatus: CompensationSummaryComparisonStatus;
  comparisonLabel: string;
}

interface CompensationSummaryComparisonDataSource extends Datatable {
  data: CompensationSummaryComparisonRow[];
  offset: number;
}

interface CompensationCourseSummaryDataSource extends Datatable {
  data: CompensationCourseSummaryRow[];
  offset: number;
}

interface CompensationCourseSummaryRow {
  rowType: CompensationCourseSummaryRowType;
  level?: string;
  subjectCode?: string;
  subjectName?: string;
  teachingType?: string;
  formulaLabel?: string;
  workloadSharePercentage?: number | null;
  payablePercentage?: number | null;
  studentCount: number;
  minimumHours: number;
  scheduledHours: number;
  makeupHours: number;
  totalHours: number;
  excessHours: number;
  payableHours: number;
  rateLabel: string;
  compensationAmount: number;
}

interface CompensationOverviewRateBreakdown {
  level: string;
  payableHours: number;
  rate: number;
  amount: number;
}

@Component({
  selector: 'app-compensation-summary',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  templateUrl: './compensation-summary.component.html',
  styleUrl: './compensation-summary.component.scss',
})
export class CompensationSummaryComponent implements OnInit, OnDestroy {
  @ViewChild('comparisonIndexTemplate', { static: true })
  comparisonIndexTemplate!: TemplateRef<any>;
  @ViewChild('comparisonSystemRequestTemplate', { static: true })
  comparisonSystemRequestTemplate!: TemplateRef<any>;
  @ViewChild('comparisonErpRequestTemplate', { static: true })
  comparisonErpRequestTemplate!: TemplateRef<any>;
  @ViewChild('comparisonOfficialTripRequestTemplate', { static: true })
  comparisonOfficialTripRequestTemplate!: TemplateRef<any>;
  @ViewChild('comparisonLeaveDateTemplate', { static: true })
  comparisonLeaveDateTemplate!: TemplateRef<any>;
  @ViewChild('comparisonMakeupTeachingTemplate', { static: true })
  comparisonMakeupTeachingTemplate!: TemplateRef<any>;
  @ViewChild('comparisonCalculatedMakeupHoursTemplate', { static: true })
  comparisonCalculatedMakeupHoursTemplate!: TemplateRef<any>;
  @ViewChild('comparisonStatusTemplate', { static: true })
  comparisonStatusTemplate!: TemplateRef<any>;
  @ViewChild('courseLevelTemplate', { static: true })
  courseLevelTemplate!: TemplateRef<any>;
  @ViewChild('courseSubjectCodeTemplate', { static: true })
  courseSubjectCodeTemplate!: TemplateRef<any>;
  @ViewChild('courseSubjectNameTemplate', { static: true })
  courseSubjectNameTemplate!: TemplateRef<any>;
  @ViewChild('courseStudentCountTemplate', { static: true })
  courseStudentCountTemplate!: TemplateRef<any>;
  @ViewChild('courseNumberTemplate', { static: true })
  courseNumberTemplate!: TemplateRef<any>;
  @ViewChild('courseNonNegativeNumberTemplate', { static: true })
  courseNonNegativeNumberTemplate!: TemplateRef<any>;
  @ViewChild('coursePayableHoursTemplate', { static: true })
  coursePayableHoursTemplate!: TemplateRef<any>;
  @ViewChild('courseRateTemplate', { static: true })
  courseRateTemplate!: TemplateRef<any>;
  @ViewChild('courseCompensationAmountTemplate', { static: true })
  courseCompensationAmountTemplate!: TemplateRef<any>;

  requestFilterForm!: FormGroup;
  compensationFilterForm!: FormGroup;
  summary = signal<CompensationSummaryResponse>(this.createEmptySummary());
  isLoading = signal(false);
  hasLoadError = signal(false);
  isLoadingErpComparisonDetail = signal(false);
  hasErpComparisonDetailError = signal(false);
  activeTab = signal<CompensationSummaryTab>('requests');
  activeCourseSummaryTab = signal<CompensationCourseSummaryTab>('regular');
  pdfPreviewCourseSummaryTab = signal<CompensationCourseSummaryTab>('regular');
  isPdfPreviewModalOpen = signal(false);
  pdfLoading = signal(false);
  pdfError = signal(false);
  pdfPreviewUrl = signal<SafeResourceUrl | null>(null);
  readonly erpComparisonDescription =
    'รายการในตารางนี้เป็นคำขอสอนชดเชยที่ตรงกับข้อมูล ERP หรือไปปฏิบัติงาน';
  academicTermOptions: string[] = [];
  yearOptions: string[] = [];
  comparisonColumns: TableColumn[] = [];
  regularSummaryColumns: TableColumn[] = [];
  regularLineItemColumns: TableColumn[] = [];
  regularSummaryTableDatasource: CompensationCourseSummaryDataSource = this.createCourseSummaryTableDatasource();
  regularLineItemTableDatasource: CompensationCourseSummaryDataSource = this.createCourseSummaryTableDatasource();
  internationalLineItemTableDatasource: CompensationCourseSummaryDataSource = this.createCourseSummaryTableDatasource();
  erpComparisonDetail?: CompensationSummaryErpComparisonDetail;
  comparisonRows: CompensationSummaryComparisonRow[] = [];
  comparisonPageSizeOptions = [5, 10, 20];
  comparisonCurrentPage = 1;
  comparisonCurrentLimit = 5;
  comparisonSortParams: DatatableSortParams = { sort: null, direction: null };
  comparisonTableDatasource: CompensationSummaryComparisonDataSource = this.createComparisonTableDatasource();

  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private studyPeriodService = inject(StudyPeriodService);
  private teachCompensationService = inject(TeachCompensationService);
  private sanitizer = inject(DomSanitizer);
  private academicTerms: AcademicTermYearModel[] = [];
  private rawPdfPreviewUrl: string | null = null;
  private pdfPreviewRequestId = 0;

  ngOnInit(): void {
    this.initForm();
    this.initComparisonColumns();
    this.initCourseSummaryColumns();
    this.applyCourseSummaryTables(this.summary());
    this.listenFilters();
    this.loadAcademicTerms();
  }

  ngOnDestroy(): void {
    this.pdfPreviewRequestId += 1;
    this.revokePdfPreviewUrl();
  }

  onOpenPdfPreview(): void {
    if (this.pdfLoading()) {
      return;
    }

    const courseSummaryTab = this.activeCourseSummaryTab();
    const pdfRequest = courseSummaryTab === 'regular'
      ? this.teachCompensationService.downloadRegularCompensationCalculationPdf(
          this.buildAcademicTermFilterParams(this.compensationFilterForm),
        )
      : this.teachCompensationService.downloadInternationalCompensationCalculationPdf(
          this.buildAcademicTermFilterParams(this.compensationFilterForm),
        );

    this.pdfPreviewCourseSummaryTab.set(courseSummaryTab);
    this.isPdfPreviewModalOpen.set(true);
    this.pdfLoading.set(true);
    this.pdfError.set(false);
    const requestId = ++this.pdfPreviewRequestId;

    pdfRequest.pipe(
      take(1),
      finalize(() => {
        if (requestId === this.pdfPreviewRequestId) {
          this.pdfLoading.set(false);
        }
      }),
    ).subscribe({
      next: (blob) => {
        if (requestId !== this.pdfPreviewRequestId || !this.isPdfPreviewModalOpen()) {
          return;
        }

        this.revokePdfPreviewUrl();
        this.rawPdfPreviewUrl = window.URL.createObjectURL(blob);
        this.pdfPreviewUrl.set(
          this.sanitizer.bypassSecurityTrustResourceUrl(`${this.rawPdfPreviewUrl}#toolbar=0&navpanes=0`),
        );
      },
      error: (err) => {
        if (requestId !== this.pdfPreviewRequestId || !this.isPdfPreviewModalOpen()) {
          return;
        }

        console.error(err);
        this.revokePdfPreviewUrl();
        this.pdfError.set(true);
      },
    });
  }

  onClosePdfPreview(): void {
    this.pdfPreviewRequestId += 1;
    this.isPdfPreviewModalOpen.set(false);
    this.pdfError.set(false);
    this.revokePdfPreviewUrl();
  }

  pdfPreviewTitle(): string {
    return this.pdfPreviewCourseSummaryTab() === 'regular'
      ? 'ตัวอย่างเอกสารคำนวณค่าสอนเกินภาระงาน'
      : 'ตัวอย่างรายงานภาระการสอนหลักสูตรนานาชาติ';
  }

  formatHours(value: number): string {
    const numericValue = this.normalizeNumber(value);
    const formattedValue = Number.isInteger(numericValue)
      ? String(numericValue)
      : numericValue.toFixed(2).replace(/\.?0+$/, '');

    return `${formattedValue} ชม.`;
  }

  formatTableNumber(value: number | string | null | undefined): string {
    const numericValue = this.normalizeNumber(value);

    return Number.isInteger(numericValue)
      ? String(numericValue)
      : numericValue.toFixed(2).replace(/\.?0+$/, '');
  }

  formatNonNegativeTableNumber(value: number | string | null | undefined): string {
    return this.formatTableNumber(Math.max(this.normalizeNumber(value), 0));
  }

  formatCurrency(value: number | null | undefined): string {
    return this.formatDisplayCurrency(value);
  }

  formatDisplayCurrency(value: number | null | undefined): string {
    return new Intl.NumberFormat('th-TH', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(this.normalizeNumber(value));
  }

  formatStudents(value: number | null | undefined): string {
    return `${this.formatTableNumber(value)} คน`;
  }

  regularWeeklyWorkloadRows(): CompensationSummaryRegularWeeklyWorkloadItem[] {
    const workload = this.regularWeeklyWorkload();

    return [
      ...workload.course_items,
      ...(workload.undergraduate_project_course_items ?? []),
    ];
  }

  regularWeeklyWorkload(): CompensationSummaryRegularWeeklyWorkload {
    return this.summary().regular_weekly_workload ?? this.emptyRegularWeeklyWorkload();
  }

  // ADR-EF: บวกจากแถวที่ตารางนี้แสดงเองล้วน ๆ + ชดเชย ไม่อ่าน total_weekly_hours เพราะ snapshot ที่
  // freeze ไว้ก่อน ADR-EF ยังนับชั่วโมงที่ปรึกษาโครงงาน ป.ตรี แทนชั่วโมงตัววิชา
  regularWeeklyWorkloadDisplayTotal(): number {
    const rowsTotal = this.regularWeeklyWorkloadRows().reduce(
      (total, item) => total + this.normalizeNumber(item.weekly_hours),
      0,
    );

    return rowsTotal +
      this.regularProjectResearchWeeklyHours() +
      (this.hasAdministrationWorkload()
        ? this.normalizeNumber(this.regularWeeklyWorkload().administration_weekly_hours)
        : 0) +
      this.normalizeNumber(this.internationalWeeklyWorkload().total_offset_weekly_hours);
  }

  regularProjectResearchPrimaryAdvisorStudentCount(): number {
    const workload = this.regularWeeklyWorkload();

    return this.normalizeNumber(workload.project_research_primary_advisor_student_count) -
      this.normalizeNumber(workload.undergraduate_project_research_primary_advisor_student_count);
  }

  regularProjectResearchCoAdvisorStudentCount(): number {
    const workload = this.regularWeeklyWorkload();

    return this.normalizeNumber(workload.project_research_co_advisor_student_count) -
      this.normalizeNumber(workload.undergraduate_project_research_co_advisor_student_count);
  }

  regularProjectResearchStudentCount(): number {
    const workload = this.regularWeeklyWorkload();

    return this.normalizeNumber(workload.project_research_student_count) -
      this.normalizeNumber(workload.undergraduate_project_research_student_count);
  }

  regularProjectResearchWeeklyHours(): number {
    const workload = this.regularWeeklyWorkload();

    return this.normalizeNumber(workload.project_research_weekly_hours) -
      this.normalizeNumber(workload.undergraduate_project_research_advisor_weekly_hours);
  }

  hasAdministrationWorkload(): boolean {
    return Boolean(this.regularWeeklyWorkload().administration_position_code);
  }

  administrationWorkloadPercentage(): number | null {
    if (!this.hasAdministrationWorkload()) {
      return null;
    }

    const minimumWeeklyHours = this.normalizeNumber(
      this.internationalWeeklyWorkload().minimum_weekly_hours,
    );
    if (minimumWeeklyHours <= 0) {
      return null;
    }

    const administrationWeeklyHours = this.normalizeNumber(
      this.regularWeeklyWorkload().administration_weekly_hours,
    );

    return administrationWeeklyHours * 100 / minimumWeeklyHours;
  }

  administrationWorkloadLabel(): string {
    const percentage = this.administrationWorkloadPercentage();
    const workload = this.regularWeeklyWorkload();
    const configuredLabel = workload.administration_position_label?.trim();
    const positionCode = workload.administration_position_code?.trim();
    const positionLabel = configuredLabel != null && configuredLabel !== ''
      ? configuredLabel
      : positionCode;

    if (percentage === null || positionLabel == null || positionLabel === '') {
      return '-';
    }

    return `ตำแหน่ง: ${positionLabel} (ร้อยละ ${this.formatTableNumber(percentage)})`;
  }

  formatOptionalTableNumber(value: number | string | null | undefined): string {
    return value == null ? '-' : this.formatTableNumber(value);
  }

  formatWorkloadPercentage(value: number | string | null | undefined): string {
    return value == null ? '-' : `${this.formatTableNumber(value)}%`;
  }

  remainingWeeklyPercentage(
    row: CompensationSummaryInternationalWeeklyWorkloadItem,
  ): number | null {
    if (row.weekly_hours == null || row.payable_weekly_hours == null) {
      return null;
    }

    const weeklyHours = this.normalizeNumber(row.weekly_hours);
    if (weeklyHours <= 0) {
      return 100;
    }

    const payableHours = this.normalizeNumber(row.payable_weekly_hours);
    const percentage = (payableHours / weeklyHours) * 100;

    return Math.min(Math.max(percentage, 0), 100);
  }

  // ADR-EF: คอลัมน์ "เหลือเบิกจ่าย" = สัดส่วนภาระงาน × คงเหลือร้อยละ ตรงกับตัวคูณเงินของ ADR-DZ
  // ในแถวที่ถูกนำไปชดเชย และเท่ากับสัดส่วนภาระงานเดิมในแถวที่ไม่ถูกชดเชย (คงเหลือ = 100%)
  payableSharePercentage(
    row: CompensationSummaryInternationalWeeklyWorkloadItem,
  ): number | null {
    const remaining = this.remainingWeeklyPercentage(row);
    if (remaining == null) {
      return null;
    }

    if (row.workload_share_percentage == null) {
      return remaining;
    }

    const percentage =
      (this.normalizeNumber(row.workload_share_percentage) * remaining) / 100;

    return Math.min(Math.max(percentage, 0), 100);
  }

  internationalWeeklyWorkloadRows(): CompensationSummaryInternationalWeeklyWorkloadItem[] {
    const workload = this.internationalWeeklyWorkload();

    return [
      ...workload.course_items,
      ...(workload.undergraduate_project_course_items ?? []),
    ];
  }

  internationalWeeklyWorkload(): CompensationSummaryInternationalWeeklyWorkload {
    return this.summary().international_weekly_workload ?? this.emptyInternationalWeeklyWorkload();
  }

  formatRate(value: string | null | undefined): string {
    return value == null || value === '' ? '-' : value;
  }

  totalCompensationAmount(): number {
    const summary = this.summary();
    const explicitTotal = this.normalizeNumber(summary.regular_amount);

    if (explicitTotal > 0) {
      return explicitTotal;
    }

    return this.normalizeNumber(summary.regular_summary?.payable_amount);
  }

  eligibleMakeupTeachingWorkload(): number {
    const regularSummary = this.summary().regular_summary;

    if (regularSummary != null) {
      return this.sumRegularLevelValue('makeup_hours');
    }

    return this.normalizeNumber(
      this.summary().eligible_makeup_teaching_workload ?? this.summary().makeup_teaching_workload,
    );
  }

  calculationTeachingWorkload(): number {
    const regularSummary = this.summary().regular_summary;

    if (regularSummary != null) {
      return this.sumRegularLevelValue('scheduled_hours');
    }

    return Math.max(this.calculationTotalTeachingWorkload() - this.eligibleMakeupTeachingWorkload(), 0);
  }

  calculationTotalTeachingWorkload(): number {
    const regularSummary = this.summary().regular_summary;

    if (regularSummary != null) {
      return this.sumRegularLevelValue('total_hours');
    }

    return this.normalizeNumber(
      this.summary().calculation_total_teaching_workload ?? this.summary().total_teaching_workload,
    );
  }

  calculationExcessHours(): number {
    const regularSummary = this.summary().regular_summary;

    if (regularSummary != null) {
      return Math.max(this.calculationTotalTeachingWorkload() - this.minimumTeachingWorkload(), 0);
    }

    return this.normalizeNumber(
      this.summary().calculation_excess_hours ?? this.summary().excess_hours,
    );
  }

  minimumTeachingWorkload(): number {
    const regularSummary = this.summary().regular_summary;

    if (regularSummary != null) {
      return this.sumRegularLevelValue('minimum_hours');
    }

    return this.normalizeNumber(this.summary().minimum_teaching_workload);
  }

  calculationWorkloadShortfall(): number {
    return Math.max(this.minimumTeachingWorkload() - this.calculationTotalTeachingWorkload(), 0);
  }

  hasPayableExcessHours(): boolean {
    return this.calculationExcessHours() > 0;
  }

  regularRateBreakdown(): CompensationOverviewRateBreakdown[] {
    const regularSummary = this.summary().regular_summary;

    if (regularSummary == null) {
      return [];
    }

    return [
      { level: 'ป.ตรี', value: regularSummary.undergraduate },
      { level: 'ป.โท-เอก', value: regularSummary.graduate },
    ].map(({ level, value }) => ({
      level,
      payableHours: this.normalizeNumber(value?.payable_hours),
      rate: this.normalizeNumber(value?.rate),
      amount: this.normalizeNumber(value?.amount ?? value?.compensation_amount),
    })).filter((item) => item.payableHours > 0 || item.amount > 0);
  }

  hasMultipleRegularRates(): boolean {
    return this.regularRateBreakdown().length > 1;
  }

  regularRawCompensationAmount(): number {
    const regularSummary = this.summary().regular_summary;
    const rawAmount = this.normalizeNumber(regularSummary?.raw_amount);

    if (rawAmount > 0) {
      return rawAmount;
    }

    const breakdownTotal = this.regularRateBreakdown()
      .reduce((total, item) => total + item.amount, 0);

    return breakdownTotal > 0 ? breakdownTotal : this.totalCompensationAmount();
  }

  regularMaximumCompensationAmount(): number {
    return this.normalizeNumber(this.summary().regular_summary?.max_amount);
  }

  isRegularCompensationCapped(): boolean {
    return this.regularMaximumCompensationAmount() > 0 &&
      this.regularRawCompensationAmount() > this.totalCompensationAmount();
  }

  shouldShowOverview(): boolean {
    return this.activeCourseSummaryTab() === 'regular';
  }

  getCourseSummaryTitle(): string {
    return this.activeCourseSummaryTab() === 'regular'
      ? 'หลักสูตรปกติ'
      : 'หลักสูตรนานาชาติ';
  }

  getCourseSummaryDescription(): string {
    return this.activeCourseSummaryTab() === 'regular'
      ? 'สรุปตามระดับการศึกษา'
      : 'แสดงตามรายวิชา';
  }

  getCourseSummaryCellClass(
    row: CompensationCourseSummaryRow | undefined,
    columnClass = '',
  ): string {
    return [
      'compensation-summary-course-datatable-cell',
      row == null ? '' : `compensation-summary-course-datatable-cell-${row.rowType}`,
      columnClass,
    ].filter(Boolean).join(' ');
  }

  onCourseSummaryTabChange(tab: CompensationCourseSummaryTab): void {
    if (this.activeCourseSummaryTab() === tab) {
      return;
    }

    this.activeCourseSummaryTab.set(tab);
  }

  formatDate(value: string | null | undefined): string {
    if (value == null || value === '') {
      return '-';
    }

    const parts = value.split('-');
    if (parts.length !== 3) {
      return value;
    }

    const [year, month, day] = parts;
    return `${day}/${month}/${Number(year) + 543}`;
  }

  formatDateTime(value: string | null | undefined): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear() + 543;
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');

    return `${day}/${month}/${year} ${hour}:${minute}`;
  }

  formatRequestTitle(request: CompensationSummaryErpApprovedRequest): string {
    return [request.subject_code, request.subject_name].filter(Boolean).join(' - ') || '-';
  }

  formatScheduledTeachingSlots(request: CompensationSummaryErpApprovedRequest): string[] {
    return (request.scheduled_teaching_slots ?? []).map((slot) => {
      const timeRange = [slot.start_time, slot.end_time].filter(Boolean).join(' - ');
      const hours = this.formatHours(this.normalizeNumber(slot.hours));
      const weekday = slot.day_of_week == null ? null : THAI_WEEKDAY_NAMES[slot.day_of_week] ?? null;
      const scheduleLabel = [weekday, timeRange].filter(Boolean).join(' ');

      return scheduleLabel === '' ? hours : `${scheduleLabel} (${hours})`;
    });
  }

  formatCalculatedMakeupHours(row: CompensationSummaryComparisonRow): string {
    if (row.systemRequest == null) {
      return '-';
    }

    return this.formatHours(this.normalizeNumber(row.systemRequest.calculated_makeup_hours));
  }

  formatRequestStatus(status: string | null | undefined): string {
    const labels: Record<string, string> = {
      approved: 'รับรองแล้ว',
      submitted: 'รอดำเนินการ',
      draft: 'รอดำเนินการ',
      rejected: 'ไม่อนุมัติ',
      revision_requested: 'ส่งกลับแก้ไข',
    };

    return status == null || status === '' ? '-' : labels[status] ?? status;
  }

  getRequestStatusBadgeClass(status: string | null | undefined): string {
    const classes: Record<string, string> = {
      approved: 'compensation-summary-request-status-badge-success',
      submitted: 'compensation-summary-request-status-badge-pending',
      draft: 'compensation-summary-request-status-badge-pending',
      rejected: 'compensation-summary-request-status-badge-danger',
      revision_requested: 'compensation-summary-request-status-badge-revision',
    };

    return status == null || status === ''
      ? 'compensation-summary-request-status-badge-neutral'
      : classes[status] ?? 'compensation-summary-request-status-badge-neutral';
  }

  formatMakeupTime(request: CompensationSummaryErpApprovedRequest): string {
    const date = this.formatDate(request.makeup_teaching_date);
    const timeRange = [request.makeup_start_time, request.makeup_end_time].filter(Boolean).join(' - ');

    return timeRange !== '' ? `${date} ${timeRange}` : date;
  }

  formatIsActive(value: boolean | null | undefined): string {
    if (value === true) {
      return 'ใช้งาน';
    }

    if (value === false) {
      return 'ยกเลิก';
    }

    return '-';
  }

  formatRowNumber(value: number | null | undefined): number | string {
    return value ?? '-';
  }

  formatLeaveDate(row: CompensationSummaryComparisonRow): string {
    return this.formatDate(row.leaveDate);
  }

  formatErpLeaveDate(row: CompensationSummaryComparisonRow): string {
    return this.formatExternalLeaveDate(row.erpRequest);
  }

  formatOfficialTripDate(row: CompensationSummaryComparisonRow): string {
    return this.formatExternalLeaveDate(row.officialTripRequest);
  }

  formatMakeupTeachingDateTime(row: CompensationSummaryComparisonRow): string {
    if (row.systemRequest == null) {
      return '-';
    }

    const makeupTime = this.formatMakeupTime(row.systemRequest);
    return makeupTime !== '-' ? makeupTime : '-';
  }

  formatMakeupTeachingDate(row: CompensationSummaryComparisonRow): string {
    if (row.systemRequest == null) {
      return '-';
    }

    return this.formatDate(row.systemRequest.makeup_teaching_date);
  }

  formatMakeupTeachingTimeWithHours(row: CompensationSummaryComparisonRow): string {
    if (row.systemRequest == null) {
      return '-';
    }

    const timeRange = [row.systemRequest.makeup_start_time, row.systemRequest.makeup_end_time]
      .filter(Boolean)
      .join(' - ');

    if (timeRange === '') {
      return '-';
    }

    const hours = this.formatMakeupHours(row);
    return hours === '-' ? timeRange : `${timeRange} (${hours})`;
  }

  formatMakeupHours(row: CompensationSummaryComparisonRow): string {
    if (row.systemRequest == null) {
      return '-';
    }

    const startMinutes = this.timeToMinutes(row.systemRequest.makeup_start_time);
    const endMinutes = this.timeToMinutes(row.systemRequest.makeup_end_time);

    if (startMinutes == null || endMinutes == null || endMinutes <= startMinutes) {
      return '-';
    }

    return this.formatHours((endMinutes - startMinutes) / 60);
  }

  getComparisonBadgeClass(status: CompensationSummaryComparisonStatus): string {
    const classes: Record<CompensationSummaryComparisonStatus, string> = {
      matched: 'compensation-summary-comparison-badge-success',
      missing_erp: 'compensation-summary-comparison-badge-danger',
      not_approved: 'compensation-summary-comparison-badge-danger',
      missing_approved_request: 'compensation-summary-comparison-badge-warning',
    };

    return classes[status];
  }

  getErpComparisonTitle(): string {
    const title = ' ผลตรวจสอบข้อมูลวันลาและไปปฏิบัติงาน';
    const academicTerm = String(this.requestFilterForm.get('academicTerm')?.value ?? '').trim();
    const year = String(this.requestFilterForm.get('year')?.value ?? '').trim();

    return academicTerm !== '' && year !== ''
      ? `${title} ${academicTerm}/${year}`
      : title;
  }

  onTabChange(tab: CompensationSummaryTab): void {
    if (this.activeTab() === tab) {
      return;
    }

    this.activeTab.set(tab);
    this.loadSummary(tab);
  }

  onComparisonPageChange(page: number): void {
    if (
      page < 1 ||
      page > this.comparisonTableDatasource.total_page ||
      page === this.comparisonCurrentPage
    ) {
      return;
    }

    this.loadErpComparisonDetail(page);
  }

  onComparisonLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit <= 0 || limit === this.comparisonCurrentLimit) {
      return;
    }

    this.comparisonCurrentLimit = limit;
    this.comparisonCurrentPage = 1;
    this.loadErpComparisonDetail(1);
  }

  onComparisonSortChange(event: DatatableSortChangeEvent): void {
    this.comparisonSortParams = createDatatableSortParams(event);
    this.comparisonCurrentPage = 1;
    this.loadErpComparisonDetail(1);
  }

  loadErpComparisonDetail(page = this.comparisonCurrentPage): void {
    if (this.isLoadingErpComparisonDetail() || !this.summary().erp_comparison.has_result) {
      return;
    }

    const hasExistingDetail = this.erpComparisonDetail != null;
    this.comparisonCurrentPage = page;

    if (!hasExistingDetail) {
      this.comparisonRows = [];
      this.comparisonTableDatasource = this.createComparisonTableDatasource();
    }

    this.isLoadingErpComparisonDetail.set(true);
    this.hasErpComparisonDetailError.set(false);

    this.teachCompensationService.getCompensationSummaryErpComparison(
      this.buildErpComparisonFilterParams(),
    ).pipe(
      finalize(() => this.isLoadingErpComparisonDetail.set(false)),
    ).subscribe({
      next: (detail) => {
        this.erpComparisonDetail = {
          ...detail,
          matched: detail.matched ?? [],
          import_unmatched: detail.import_unmatched ?? [],
          system_unmatched: detail.system_unmatched ?? [],
        };
        this.applyComparisonTable(detail.comparison_table);
      },
      error: (err) => {
        console.error(err);
        if (!hasExistingDetail) {
          this.clearErpComparisonDetail();
        }
        this.hasErpComparisonDetailError.set(true);
      },
    });
  }

  mapComparisonTableRow(row: CompensationSummaryErpComparisonTableRow): CompensationSummaryComparisonRow {
    const erpRequest = row.erp_request;
    const officialTripRequest = row.official_trip_request;

    return {
      id: String(row.id),
      sequence: row.sequence,
      systemRequest: row.system_request,
      erpRequest,
      officialTripRequest,
      leaveDate: row.leave_date,
      comparisonStatus: row.comparison_status,
      comparisonLabel: this.comparisonSourceLabel(
        row.comparison_status,
        row.comparison_label,
        erpRequest,
        officialTripRequest,
      ),
    };
  }

  createComparisonTableDatasource(
    rows: CompensationSummaryComparisonRow[] = [],
    page = 1,
    limit = this.comparisonCurrentLimit,
    total = rows.length,
    offset = (page - 1) * limit,
  ): CompensationSummaryComparisonDataSource {
    const totalPage = Math.max(Math.ceil(total / limit), 1);
    const normalizedPage = Math.min(Math.max(page, 1), totalPage);

    return {
      data: rows,
      page: normalizedPage,
      limit,
      total,
      total_page: totalPage,
      offset: normalizedPage === page ? offset : (normalizedPage - 1) * limit,
    };
  }

  getUnsuccessfulComparisonCount(detail: CompensationSummaryErpComparisonDetail): number {
    return this.buildComparisonRows(detail)
      .filter((row) => row.comparisonStatus !== 'matched')
      .length;
  }

  buildComparisonRows(detail: CompensationSummaryErpComparisonDetail): CompensationSummaryComparisonRow[] {
    const erpRowsByLeaveDate = this.indexErpRowsByLeaveDate(detail);
    const rows: Omit<CompensationSummaryComparisonRow, 'sequence'>[] = [];
    const consumedErpRows = new Set<CompensationSummaryErpRowResult>();

    (detail.matched ?? []).forEach((erpRow) => {
      (erpRow.approved_requests ?? []).forEach((request) => {
        const erpRequest = erpRow.erp_record ?? (erpRow.leave_record_id != null ? erpRow : null);
        const officialTripRequest = erpRow.official_trip_record ?? null;
        const leaveDate = request.leave_date !== ''
          ? request.leave_date
          : erpRequest?.leave_date != null && erpRequest.leave_date !== ''
            ? erpRequest.leave_date
            : officialTripRequest?.leave_date ?? null;

        rows.push({
          id: `matched-${erpRequest?.leave_record_id ?? officialTripRequest?.leave_record_id ?? request.id}-${request.id}`,
          systemRequest: request,
          erpRequest,
          officialTripRequest,
          leaveDate,
          comparisonStatus: 'matched',
          comparisonLabel: this.comparisonSourceLabel(
            'matched',
            'ตรงกัน',
            erpRequest,
            officialTripRequest,
          ),
        });
        if (erpRequest != null) {
          consumedErpRows.add(erpRow);
        }
      });
    });

    (detail.system_unmatched ?? []).forEach((request) => {
      const erpRow = erpRowsByLeaveDate.get(request.leave_date) ?? null;
      const isNotApproved = request.reason === 'คำขอยังไม่อนุมัติ';

      if (erpRow != null) {
        consumedErpRows.add(erpRow);
      }

      rows.push({
        id: `system-${request.id}`,
        systemRequest: request,
        erpRequest: erpRow,
        officialTripRequest: null,
        leaveDate: request.leave_date !== '' ? request.leave_date : erpRow?.leave_date ?? null,
        comparisonStatus: isNotApproved ? 'not_approved' : 'missing_erp',
        comparisonLabel: isNotApproved ? 'ยังไม่รับรอง' : 'ไม่พบใน ERP',
      });
    });

    (detail.import_unmatched ?? []).forEach((erpRow) => {
      if (consumedErpRows.has(erpRow)) {
        return;
      }

      rows.push({
        id: `erp-${erpRow.leave_record_id ?? erpRow.schedule_submission_id}-${erpRow.leave_date}-${erpRow.row ?? 'db'}`,
        systemRequest: null,
        erpRequest: erpRow,
        officialTripRequest: null,
        leaveDate: erpRow.leave_date,
        comparisonStatus: 'missing_approved_request',
        comparisonLabel: 'ไม่พบคำขอในระบบ',
      });
    });

    return rows
      .sort((left, right) => this.compareComparisonRows(left, right))
      .map((row, index) => ({
        ...row,
        sequence: index + 1,
      }));
  }

  private initForm(): void {
    const defaultAcademicTerm = this.getDefaultAcademicTermName();
    const defaultYear = this.getDefaultBuddhistYear();

    this.academicTermOptions = [defaultAcademicTerm];
    this.yearOptions = [defaultYear];
    this.requestFilterForm = this.fb.group({
      academicTerm: [defaultAcademicTerm],
      year: [defaultYear],
    });
    this.compensationFilterForm = this.fb.group({
      academicTerm: [defaultAcademicTerm],
      year: [defaultYear],
    });
  }

  private initComparisonColumns(): void {
    this.comparisonColumns = [
      {
        prop: 'comparisonSequence',
        name: 'ลำดับ',
        sortable: true,
        cellTemplate: this.comparisonIndexTemplate,
        width: 64,
        minWidth: 64,
        maxWidth: 64,
        cellClass: 'text-center compensation-summary-center-cell',
        headerClass: 'text-center compensation-summary-center-header',
      },
      {
        prop: 'systemRequest',
        name: 'รายวิชาที่สอนชดเชย',
        sortable: true,
        cellTemplate: this.comparisonSystemRequestTemplate,
        width: 330,
        minWidth: 300,
        flexGrow: 2,
        cellClass: 'compensation-summary-comparison-detail-cell',
        headerClass: 'compensation-summary-text-header',
      },
      {
        prop: 'leaveDate',
        name: 'วันที่ขอลา/หยุด',
        sortable: true,
        cellTemplate: this.comparisonLeaveDateTemplate,
        width: 180,
        minWidth: 160,
        flexGrow: 1,
        cellClass: 'compensation-summary-left-cell',
        headerClass: 'compensation-summary-left-header',
      },

      {
        prop: 'erpRequest',
        name: 'วันที่ขอลาจาก ERP',
        sortable: true,
        cellTemplate: this.comparisonErpRequestTemplate,
        width: 220,
        minWidth: 200,
        flexGrow: 1,
        cellClass: 'compensation-summary-left-cell',
        headerClass: 'compensation-summary-left-header',
      },
      {
        prop: 'officialTripRequest',
        name: 'ไปปฏิบัติงาน',
        sortable: false,
        cellTemplate: this.comparisonOfficialTripRequestTemplate,
        width: 220,
        minWidth: 200,
        flexGrow: 1,
        cellClass: 'compensation-summary-left-cell',
        headerClass: 'compensation-summary-left-header',
      },
      {
        prop: 'makeupTeachingDate',
        name: 'วันขอสอนชดเชย',
        sortable: true,
        cellTemplate: this.comparisonMakeupTeachingTemplate,
        width: 220,
        minWidth: 200,
        flexGrow: 1,
        cellClass: 'text-center compensation-summary-center-cell',
        headerClass: 'text-center compensation-summary-center-header',
      },
      {
        prop: 'calculatedMakeupHours',
        name: 'นำไปคิดค่าภาระงาน',
        sortable: false,
        cellTemplate: this.comparisonCalculatedMakeupHoursTemplate,
        width: 190,
        minWidth: 170,
        flexGrow: 1,
        cellClass: 'text-center compensation-summary-center-cell',
        headerClass: 'text-center compensation-summary-center-header',
      },

    ];
  }

  private comparisonSourceLabel(
    status: CompensationSummaryComparisonStatus,
    fallbackLabel: string,
    erpRequest: CompensationSummaryErpRowResult | null,
    officialTripRequest: CompensationSummaryErpRowResult | null,
  ): string {
    if (status !== 'matched') {
      return fallbackLabel;
    }

    if (erpRequest != null && officialTripRequest != null) {
      return 'ตรงกับ ERP และไปปฏิบัติงาน';
    }

    if (erpRequest != null) {
      return 'ตรงกับ ERP';
    }

    if (officialTripRequest != null) {
      return 'ตรงกับไปปฏิบัติงาน';
    }

    return fallbackLabel;
  }

  private formatExternalLeaveDate(request: CompensationSummaryErpRowResult | null): string {
    const fallbackLeaveDate = request?.leave_date;
    const startValue = request?.erp_leave_start_date != null && request.erp_leave_start_date !== ''
      ? request.erp_leave_start_date
      : fallbackLeaveDate;
    const endValue = request?.erp_leave_end_date != null && request.erp_leave_end_date !== ''
      ? request.erp_leave_end_date
      : fallbackLeaveDate;
    const startDate = this.formatDate(startValue);
    const endDate = this.formatDate(endValue);

    if (startDate === '-' && endDate === '-') {
      return '-';
    }

    if (startDate === endDate || endDate === '-') {
      return startDate;
    }

    if (startDate === '-') {
      return endDate;
    }

    return `${startDate} - ${endDate}`;
  }

  private initCourseSummaryColumns(): void {
    this.regularSummaryColumns = this.createRegularSummaryColumns();
    this.regularLineItemColumns = this.createRegularLineItemColumns();
  }

  private applyCourseSummaryTables(summary: CompensationSummaryResponse): void {
    this.regularSummaryTableDatasource = this.createCourseSummaryTableDatasource(
      this.buildRegularSummaryRows(summary),
    );
    this.regularLineItemTableDatasource = this.createCourseSummaryTableDatasource(
      this.buildLineItemRows(summary.regular_line_items ?? [], 'regular'),
    );
    this.internationalLineItemTableDatasource = this.createCourseSummaryTableDatasource(
      this.buildInternationalLineItemRows(summary),
    );
  }

  private createRegularSummaryColumns(): TableColumn[] {
    return [
      {
        prop: 'level',
        name: 'ระดับ',
        sortable: false,
        cellTemplate: this.courseLevelTemplate,
        width: 170,
        minWidth: 150,
        cellClass: this.courseCellClass('compensation-summary-course-datatable-cell-text'),
        headerClass: 'text-center compensation-summary-center-header',
      },
      {
        prop: 'minimumHours',
        name: 'ภาระงานสอนขั้นต่ำ',
        sortable: false,
        cellTemplate: this.courseNumberTemplate,
        width: 170,
        minWidth: 160,
        cellClass: this.courseCenteredCellClass('compensation-summary-course-datatable-cell-number'),
        headerClass: 'text-center compensation-summary-center-header',
      },
      ...this.createSharedCourseSummaryColumns({
        includeStudentCount: false,
        includePayableHours: true,
        includeFormula: false,
        useWorkloadLabels: true,
      }),
    ];
  }

  private createRegularLineItemColumns(): TableColumn[] {
    return [
      {
        prop: 'subjectCode',
        name: 'รหัสวิชา',
        sortable: false,
        cellTemplate: this.courseSubjectCodeTemplate,
        width: 150,
        minWidth: 140,
        cellClass: this.courseCellClass('compensation-summary-course-datatable-cell-subject'),
        headerClass: 'text-center compensation-summary-center-header',
      },
      {
        prop: 'subjectName',
        name: 'รายวิชา',
        sortable: false,
        cellTemplate: this.courseSubjectNameTemplate,
        width: 260,
        minWidth: 220,
        flexGrow: 1,
        cellClass: this.courseCellClass('compensation-summary-course-datatable-cell-subject'),
        headerClass: 'text-center compensation-summary-center-header',
      },
      {
        prop: 'teachingType',
        name: 'ประเภท',
        sortable: false,
        width: 130,
        minWidth: 120,
        cellClass: this.courseCenteredCellClass('compensation-summary-course-datatable-cell-text'),
        headerClass: 'text-center compensation-summary-center-header',
      },
      {
        prop: 'level',
        name: 'ระดับ',
        sortable: false,
        width: 130,
        minWidth: 120,
        cellClass: this.courseCenteredCellClass('compensation-summary-course-datatable-cell-text'),
        headerClass: 'text-center compensation-summary-center-header',
      },
      ...this.createSharedCourseSummaryColumns({
        includeStudentCount: true,
        includePayableHours: true,
        includeFormula: false,
        useWorkloadLabels: false,
      }),
    ];
  }

  private createSharedCourseSummaryColumns(
    options: {
      includeStudentCount: boolean;
      includePayableHours: boolean;
      includeFormula: boolean;
      useWorkloadLabels: boolean;
    },
  ): TableColumn[] {
    const centeredHeaderClass = 'text-center compensation-summary-center-header';
    const columns: TableColumn[] = [
      {
        prop: 'scheduledHours',
        name: options.useWorkloadLabels ? 'ภาระงานสอนตามตาราง' : 'ชั่วโมงตามตาราง',
        sortable: false,
        cellTemplate: this.courseNumberTemplate,
        width: 150,
        minWidth: 140,
        cellClass: this.courseCenteredCellClass('compensation-summary-course-datatable-cell-number'),
        headerClass: centeredHeaderClass,
      },
      {
        prop: 'makeupHours',
        name: options.useWorkloadLabels ? 'ขอสอนชดเชย' : 'ชั่วโมงชดเชย',
        sortable: false,
        cellTemplate: this.courseNumberTemplate,
        width: 140,
        minWidth: 130,
        cellClass: this.courseCenteredCellClass('compensation-summary-course-datatable-cell-number'),
        headerClass: centeredHeaderClass,
      },
      {
        prop: 'totalHours',
        name: options.useWorkloadLabels ? 'ภาระงานสอนทั้งหมด' : 'รวมชั่วโมง',
        sortable: false,
        cellTemplate: this.courseNumberTemplate,
        width: 130,
        minWidth: 120,
        cellClass: this.courseCenteredCellClass('compensation-summary-course-datatable-cell-number'),
        headerClass: centeredHeaderClass,
      },
      ...(options.includePayableHours ? [{
        prop: options.useWorkloadLabels ? 'excessHours' : 'payableHours',
        name: options.useWorkloadLabels ? 'ภาระงานสอนเกิน' : 'ชั่วโมงคิดเงิน',
        sortable: false,
        cellTemplate: options.useWorkloadLabels
          ? this.courseNonNegativeNumberTemplate
          : this.coursePayableHoursTemplate,
        width: 140,
        minWidth: 130,
        cellClass: this.courseCenteredCellClass('compensation-summary-course-datatable-cell-number'),
        headerClass: centeredHeaderClass,
      }] : []),
      {
        prop: 'rateLabel',
        name: 'อัตรา',
        sortable: false,
        cellTemplate: this.courseRateTemplate,
        width: 150,
        minWidth: 140,
        cellClass: this.courseCenteredCellClass('compensation-summary-course-datatable-cell-rate'),
        headerClass: centeredHeaderClass,
      },
      {
        prop: 'compensationAmount',
        name: 'ค่าสอนเกินภาระงาน',
        sortable: false,
        frozenRight: true,
        cellTemplate: this.courseCompensationAmountTemplate,
        width: 140,
        minWidth: 130,
        cellClass: this.courseCenteredCellClass('compensation-summary-course-datatable-cell-number'),
        headerClass: centeredHeaderClass,
      },
    ];

    if (options.includeFormula) {
      const rateColumnIndex = columns.findIndex((column) => column.prop === 'rateLabel');
      columns.splice(rateColumnIndex, 1, {
        prop: 'formulaLabel',
        name: 'สูตร',
        sortable: false,
        width: 190,
        minWidth: 170,
        cellClass: this.courseCenteredCellClass('compensation-summary-course-datatable-cell-rate'),
        headerClass: centeredHeaderClass,
      });
    }

    if (!options.includeStudentCount) {
      return columns;
    }

    return [
      {
        prop: 'studentCount',
        name: 'จำนวนนักศึกษา',
        sortable: false,
        cellTemplate: this.courseStudentCountTemplate,
        width: 150,
        minWidth: 140,
        cellClass: this.courseCenteredCellClass('compensation-summary-course-datatable-cell-number'),
        headerClass: centeredHeaderClass,
      },
      ...columns,
    ];
  }

  private buildRegularSummaryRows(summary: CompensationSummaryResponse): CompensationCourseSummaryRow[] {
    const regularSummary = summary.regular_summary;
    if (regularSummary == null) {
      return [];
    }

    const undergraduate = this.mapRegularLevelRow('ป.ตรี', regularSummary.undergraduate);
    const graduate = this.mapRegularLevelRow('ป.โท-เอก', regularSummary.graduate);
    const dataRows = [undergraduate, graduate];

    return [
      ...dataRows,
      {
        rowType: 'total',
        level: 'รวมทั้งหมด',
        studentCount: dataRows.reduce((total, row) => total + row.studentCount, 0),
        minimumHours: dataRows.reduce((total, row) => total + row.minimumHours, 0),
        scheduledHours: dataRows.reduce((total, row) => total + row.scheduledHours, 0),
        makeupHours: dataRows.reduce((total, row) => total + row.makeupHours, 0),
        totalHours: dataRows.reduce((total, row) => total + row.totalHours, 0),
        excessHours: dataRows.reduce((total, row) => total + row.totalHours, 0) -
          dataRows.reduce((total, row) => total + row.minimumHours, 0),
        payableHours: dataRows.reduce((total, row) => total + row.payableHours, 0),
        rateLabel: '-',
        compensationAmount: this.normalizeNumber(regularSummary.payable_amount),
      },
    ];
  }

  private mapRegularLevelRow(
    level: string,
    item: CompensationSummaryRegularLevel | null | undefined,
  ): CompensationCourseSummaryRow {
    const rate = this.normalizeNumber(item?.rate);
    return {
      rowType: 'data',
      level,
      studentCount: 0,
      minimumHours: this.normalizeNumber(item?.minimum_hours),
      scheduledHours: this.normalizeNumber(item?.scheduled_hours),
      makeupHours: this.normalizeNumber(item?.makeup_hours),
      totalHours: this.normalizeNumber(item?.total_hours),
      excessHours: this.normalizeNumber(item?.excess_hours),
      payableHours: this.normalizeNumber(item?.payable_hours),
      rateLabel: rate > 0 ? this.formatTableNumber(rate) : '-',
      compensationAmount: this.normalizeNumber(item?.amount ?? item?.compensation_amount),
    };
  }

  private buildLineItemRows(
    lineItems: CompensationSummaryLineItem[],
    bucket: CompensationCourseSummaryTab,
  ): CompensationCourseSummaryRow[] {
    return lineItems.map((item) => {
      const rate = this.normalizeNumber(item.rate);
      return {
        rowType: 'data',
        subjectCode: item.subject_code ?? '-',
        subjectName: item.subject_name ?? '-',
        teachingType: item.teaching_type_label ?? item.teaching_type ?? '-',
        level: this.degreeGroupLabel(item.degree_group, bucket),
        formulaLabel: item.formula_label ?? '-',
        workloadSharePercentage: item.workload_share_percentage == null
          ? null
          : this.normalizeNumber(item.workload_share_percentage),
        payablePercentage: item.payable_percentage == null
          ? null
          : this.normalizeNumber(item.payable_percentage),
        studentCount: this.normalizeNumber(item.student_count),
        minimumHours: this.normalizeNumber(item.minimum_allocated_hours),
        scheduledHours: this.normalizeNumber(item.scheduled_hours),
        makeupHours: this.normalizeNumber(item.makeup_hours),
        totalHours: this.normalizeNumber(item.total_hours),
        excessHours: this.normalizeNumber(item.excess_hours),
        payableHours: this.normalizeNumber(item.payable_hours),
        rateLabel: rate > 0 ? this.formatTableNumber(rate) : '-',
        compensationAmount: this.normalizeNumber(item.payable_amount),
      };
    });
  }

  private buildInternationalLineItemRows(
    summary: CompensationSummaryResponse,
  ): CompensationCourseSummaryRow[] {
    const dataRows = this.buildLineItemRows(
      this.sortInternationalLineItems(
        summary.international_line_items ?? [],
        summary.international_weekly_workload?.course_items ?? [],
      ),
      'international',
    );
    if (dataRows.length === 0) {
      return [];
    }

    const fallbackAmount = dataRows
      .reduce((total, row) => total + row.compensationAmount, 0);
    const compensationAmount = summary.international_amount == null
      ? fallbackAmount
      : this.normalizeNumber(summary.international_amount);

    return [
      ...dataRows,
      {
        rowType: 'total',
        subjectCode: 'รวมทั้งหมด',
        subjectName: '',
        teachingType: '',
        level: '',
        formulaLabel: '',
        workloadSharePercentage: null,
        payablePercentage: null,
        studentCount: 0,
        minimumHours: 0,
        scheduledHours: 0,
        makeupHours: 0,
        totalHours: 0,
        excessHours: 0,
        payableHours: 0,
        rateLabel: '',
        compensationAmount,
      },
    ];
  }

  private sortInternationalLineItems(
    lineItems: CompensationSummaryLineItem[],
    workloadItems: CompensationSummaryInternationalWeeklyWorkloadItem[],
  ): CompensationSummaryLineItem[] {
    const workloadOrderByGroupId = new Map<string, number>();

    workloadItems.forEach((item, index) => {
      const groupId = this.normalizeEntityId(item.subject_schedule_group_id);
      if (groupId != null && !workloadOrderByGroupId.has(groupId)) {
        workloadOrderByGroupId.set(groupId, index);
      }
    });

    return lineItems
      .map((item, index) => ({
        item,
        originalIndex: index,
        workloadIndex: workloadOrderByGroupId.get(
          this.normalizeEntityId(item.subject_schedule_group_id) ?? '',
        ) ?? Number.MAX_SAFE_INTEGER,
      }))
      .sort((left, right) =>
        left.workloadIndex - right.workloadIndex ||
        left.originalIndex - right.originalIndex)
      .map(({ item }) => item);
  }

  private degreeGroupLabel(
    degreeGroup: string | null | undefined,
    bucket: CompensationCourseSummaryTab,
  ): string {
    if (bucket === 'international') {
      return 'อินเตอร์';
    }

    const labels: Record<string, string> = {
      undergraduate: 'ป.ตรี',
      graduate: 'ป.โท-เอก',
    };

    return degreeGroup == null || degreeGroup === '' ? '-' : labels[degreeGroup] ?? degreeGroup;
  }

  private sumRegularLevelValue(key: keyof CompensationSummaryRegularLevel): number {
    const regularSummary = this.summary().regular_summary;

    if (regularSummary == null) {
      return 0;
    }

    return this.normalizeNumber(regularSummary.undergraduate?.[key]) +
      this.normalizeNumber(regularSummary.graduate?.[key]);
  }

  private courseCellClass(columnClass: string): ({ row }: { row?: CompensationCourseSummaryRow }) => string {
    return ({ row }: { row?: CompensationCourseSummaryRow }) => (
      this.getCourseSummaryCellClass(row, columnClass)
    );
  }

  private courseCenteredCellClass(columnClass: string): ({ row }: { row?: CompensationCourseSummaryRow }) => string {
    return ({ row }: { row?: CompensationCourseSummaryRow }) => (
      `${this.getCourseSummaryCellClass(row, columnClass)} text-center compensation-summary-center-cell`
    );
  }

  private courseRightAlignedCellClass(columnClass: string): ({ row }: { row?: CompensationCourseSummaryRow }) => string {
    return ({ row }: { row?: CompensationCourseSummaryRow }) => (
      `${this.getCourseSummaryCellClass(row, columnClass)} text-end compensation-summary-right-cell`
    );
  }

  private createCourseSummaryTableDatasource(
    rows: CompensationCourseSummaryRow[] = [],
  ): CompensationCourseSummaryDataSource {
    return {
      data: rows,
      page: 1,
      limit: rows.length,
      total: rows.length,
      total_page: 1,
      offset: 0,
    };
  }

  private listenFilters(): void {
    this.requestFilterForm.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        if (this.activeTab() === 'requests') {
          this.loadSummary('requests');
        }
      });

    this.compensationFilterForm.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        if (this.activeTab() === 'compensation') {
          this.loadSummary('compensation');
        }
      });
  }

  private loadAcademicTerms(): void {
    this.studyPeriodService.getAcademicTermYears()
      .pipe(take(1))
      .subscribe({
      next: (terms) => {
        this.academicTerms = terms;
        this.academicTermOptions = this.buildAcademicTermOptions(terms);
        this.yearOptions = this.buildAcademicYearOptions(terms);
        this.ensureFilterDefaults();
        this.loadSummary(this.activeTab());
      },
      error: (err) => {
        console.error(err);
        this.loadSummary(this.activeTab());
      },
    });
  }

  private buildAcademicTermOptions(terms: AcademicTermYearModel[]): string[] {
    const options = Array.from(new Set(
      terms.map((term) => String(term.name).trim()).filter(Boolean),
    )).sort((a, b) => a.localeCompare(b, 'th'));

    return options.length > 0 ? options : this.academicTermOptions;
  }

  private buildAcademicYearOptions(terms: AcademicTermYearModel[]): string[] {
    const options = Array.from(new Set(
      terms.map((term) => String(term.year + 543)),
    )).sort((a, b) => Number(b) - Number(a));

    return options.length > 0 ? options : this.yearOptions;
  }

  private ensureFilterDefaults(): void {
    this.ensureFilterDefault(this.requestFilterForm);
    this.ensureFilterDefault(this.compensationFilterForm);
  }

  private ensureFilterDefault(form: FormGroup): void {
    const academicTerm = String(form.get('academicTerm')?.value ?? '');
    const year = String(form.get('year')?.value ?? '');

    if (this.findAcademicTerm(academicTerm, year) != null || this.academicTerms.length === 0) {
      return;
    }

    const fallbackTerm = this.academicTerms[0];

    form.patchValue({
      academicTerm: String(fallbackTerm.name),
      year: String(this.toBuddhistYear(fallbackTerm.year)),
    }, { emitEvent: false });
  }

  private revokePdfPreviewUrl(): void {
    if (this.rawPdfPreviewUrl != null) {
      window.URL.revokeObjectURL(this.rawPdfPreviewUrl);
      this.rawPdfPreviewUrl = null;
    }

    this.pdfPreviewUrl.set(null);
  }

  private loadSummary(tab: CompensationSummaryTab = this.activeTab()): void {
    this.erpComparisonDetail = undefined;
    this.comparisonRows = [];
    this.resetComparisonTableState();
    this.isLoading.set(true);
    this.hasLoadError.set(false);

    this.teachCompensationService.getCompensationSummary(
      this.buildAcademicTermFilterParams(this.getFilterFormForTab(tab)),
    ).pipe(
      finalize(() => this.isLoading.set(false)),
    ).subscribe({
      next: (summary) => {
        const normalizedSummary = {
          total_request_count: this.normalizeNumber(summary.total_request_count),
          approved_request_count: this.normalizeNumber(summary.approved_request_count),
          pending_request_count: this.normalizeNumber(summary.pending_request_count),
          total_requested_hours: this.normalizeNumber(summary.total_requested_hours),
          approved_hours: this.normalizeNumber(summary.approved_hours),
          pending_hours: this.normalizeNumber(summary.pending_hours),
          minimum_teaching_workload: this.normalizeNumber(summary.minimum_teaching_workload),
          teaching_workload: this.normalizeNumber(summary.teaching_workload),
          makeup_teaching_workload: this.normalizeNumber(summary.makeup_teaching_workload),
          total_teaching_workload: this.normalizeNumber(summary.total_teaching_workload),
          excess_hours: this.normalizeNumber(summary.excess_hours),
          eligible_makeup_teaching_workload: this.normalizeNumber(
            summary.eligible_makeup_teaching_workload ?? summary.makeup_teaching_workload,
          ),
          calculation_total_teaching_workload: this.normalizeNumber(
            summary.calculation_total_teaching_workload ?? summary.total_teaching_workload,
          ),
          calculation_excess_hours: this.normalizeNumber(
            summary.calculation_excess_hours ?? summary.excess_hours,
          ),
          meets_minimum: summary.meets_minimum === true,
          external_sources_ready: summary.external_sources_ready === true,
          eligible_for_disbursement:
            summary.eligible_for_disbursement ?? (summary.meets_minimum === true),
          regular_amount: this.normalizeNumber(summary.regular_amount),
          international_amount: this.normalizeNumber(summary.international_amount),
          total_amount: this.normalizeNumber(summary.total_amount),
          regular_summary: summary.regular_summary ?? null,
          international_summary: summary.international_summary ?? null,
          regular_line_items: summary.regular_line_items ?? [],
          international_line_items: summary.international_line_items ?? [],
          regular_weekly_workload: this.normalizeRegularWeeklyWorkload(summary.regular_weekly_workload),
          international_weekly_workload: this.normalizeInternationalWeeklyWorkload(
            summary.international_weekly_workload,
          ),
          academic_term: summary.academic_term ?? null,
          erp_comparison: this.normalizeErpComparison(summary.erp_comparison),
        };

        this.summary.set(normalizedSummary);
        this.applyCourseSummaryTables(normalizedSummary);

        if (tab === 'requests' && normalizedSummary.erp_comparison.has_result) {
          this.loadErpComparisonDetail();
          return;
        }

        this.clearErpComparisonDetail();
      },
      error: (err) => {
        console.error(err);
        this.hasLoadError.set(true);
        const emptySummary = this.createEmptySummary();
        this.summary.set(emptySummary);
        this.applyCourseSummaryTables(emptySummary);
        this.clearErpComparisonDetail();
      },
    });
  }

  private indexErpRowsByLeaveDate(
    detail: CompensationSummaryErpComparisonDetail,
  ): Map<string, CompensationSummaryErpRowResult> {
    const rows = [...(detail.matched ?? []), ...(detail.import_unmatched ?? [])];

    return rows.reduce<Map<string, CompensationSummaryErpRowResult>>((result, row) => {
      if (row.leave_date !== '' && !result.has(row.leave_date)) {
        result.set(row.leave_date, row);
      }

      return result;
    }, new Map<string, CompensationSummaryErpRowResult>());
  }

  private compareComparisonRows(
    left: Omit<CompensationSummaryComparisonRow, 'sequence'>,
    right: Omit<CompensationSummaryComparisonRow, 'sequence'>,
  ): number {
    const leftDate = left.leaveDate ?? '';
    const rightDate = right.leaveDate ?? '';

    if (leftDate !== rightDate) {
      return leftDate.localeCompare(rightDate);
    }

    return left.id.localeCompare(right.id);
  }

  private findAcademicTerm(
    academicTermName: string,
    academicYear: string,
  ): AcademicTermYearModel | undefined {
    return this.academicTerms.find((term) => (
      String(term.name) === String(academicTermName) &&
      String(this.toBuddhistYear(term.year)) === String(academicYear)
    ));
  }

  private buildAcademicTermFilterParams(form: FormGroup): CompensationSummaryParams {
    const { academicTerm, year } = form.getRawValue();
    const normalizedAcademicTerm = String(academicTerm ?? '').trim();
    const normalizedYear = String(year ?? '').trim();
    const selectedTerm = this.findAcademicTerm(normalizedAcademicTerm, normalizedYear);

    if (selectedTerm != null) {
      return { academic_term_id: selectedTerm.id };
    }

    const params: CompensationSummaryParams = {};

    if (normalizedAcademicTerm !== '') {
      params.academic_term_name = normalizedAcademicTerm;
    }

    const numericYear = Number(normalizedYear);

    if (Number.isFinite(numericYear) && numericYear > 0) {
      params.academic_term_year = this.toChristianYear(numericYear);
    }

    return params;
  }

  private buildErpComparisonFilterParams(): CompensationSummaryParams {
    return {
      ...this.buildAcademicTermFilterParams(this.requestFilterForm),
      page: this.comparisonCurrentPage,
      limit: this.comparisonCurrentLimit,
      sort: this.comparisonSortParams.sort,
      direction: this.comparisonSortParams.direction,
      certification_status: 'approved',
    };
  }

  private getFilterFormForTab(tab: CompensationSummaryTab): FormGroup {
    return tab === 'requests' ? this.requestFilterForm : this.compensationFilterForm;
  }

  private getDefaultAcademicTermName(): string {
    const sessionAcademicTermName = String(this.authService.currentUser()?.academic_term?.name ?? '').trim();

    return sessionAcademicTermName === '' ? '1' : sessionAcademicTermName;
  }

  private getDefaultBuddhistYear(): string {
    const sessionYear = Number(this.authService.currentUser()?.academic_term?.year);

    if (!Number.isFinite(sessionYear) || sessionYear <= 0) {
      return String(this.toBuddhistYear(new Date().getFullYear()));
    }

    return String(this.toBuddhistYear(sessionYear));
  }

  private toBuddhistYear(year: number): number {
    return year > 0 && year < 2400 ? year + 543 : year;
  }

  private toChristianYear(year: number): number {
    return year >= 2400 ? year - 543 : year;
  }

  private normalizeNumber(value: unknown): number {
    const numericValue = Number(value);

    return Number.isFinite(numericValue) ? numericValue : 0;
  }

  private normalizeEntityId(value: number | string | null | undefined): string | null {
    if (value == null || value === '') {
      return null;
    }

    return String(value);
  }

  private timeToMinutes(value: string | null | undefined): number | null {
    if (value == null || value === '') {
      return null;
    }

    const [hour, minute] = value.split(':').map((part) => Number(part));

    if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
      return null;
    }

    return (hour * 60) + minute;
  }

  private normalizeErpComparison(
    value: CompensationSummaryResponse['erp_comparison'] | null | undefined,
  ): CompensationSummaryResponse['erp_comparison'] {
    const hasResult = value?.has_result;

    if (hasResult !== true) {
      return {
        has_result: false,
        system_request_count: 0,
        system_matched_request_count: 0,
        system_unmatched_request_count: 0,
        import_unmatched_count: 0,
        imported_at: null,
        result_id: null,
      };
    }

    return {
      has_result: true,
      result_id: value.result_id ?? null,
      imported_at: value.imported_at ?? null,
      system_request_count: this.normalizeNumber(value.system_request_count),
      system_matched_request_count: this.normalizeNumber(value.system_matched_request_count),
      system_unmatched_request_count: this.normalizeNumber(value.system_unmatched_request_count),
      import_unmatched_count: this.normalizeNumber(value.import_unmatched_count),
    };
  }

  private clearErpComparisonDetail(): void {
    this.erpComparisonDetail = undefined;
    this.comparisonRows = [];
    this.resetComparisonTableState();
    this.hasErpComparisonDetailError.set(false);
  }

  private resetComparisonTableState(resetSort = true): void {
    this.comparisonCurrentPage = 1;

    if (resetSort) {
      this.comparisonSortParams = { sort: null, direction: null };
    }

    this.comparisonTableDatasource = this.createComparisonTableDatasource();
  }

  private applyComparisonTable(table: CompensationSummaryErpComparisonTable | null | undefined): void {
    if (table == null) {
      this.comparisonRows = this.filterMatchedComparisonRows(this.erpComparisonDetail == null
        ? []
        : this.buildComparisonRows(this.erpComparisonDetail));
      this.comparisonTableDatasource = this.createComparisonTableDatasource(
        this.comparisonRows,
        1,
        this.comparisonCurrentLimit,
        this.comparisonRows.length,
        0,
      );
      return;
    }

    this.comparisonRows = this.filterMatchedComparisonRows(
      table.data.map((row) => this.mapComparisonTableRow(row)),
    );
    this.comparisonCurrentPage = table.page;
    this.comparisonCurrentLimit = table.limit;
    this.comparisonTableDatasource = this.createComparisonTableDatasource(
      this.comparisonRows,
      table.page,
      table.limit,
      this.comparisonRows.length,
      0,
    );
  }

  private filterMatchedComparisonRows(
    rows: CompensationSummaryComparisonRow[],
  ): CompensationSummaryComparisonRow[] {
    return rows
      .filter((row) => row.comparisonStatus === 'matched')
      .map((row, index) => ({
        ...row,
        sequence: index + 1,
      }));
  }

  private createEmptySummary(): CompensationSummaryResponse {
    return {
      total_request_count: 0,
      approved_request_count: 0,
      pending_request_count: 0,
      total_requested_hours: 0,
      approved_hours: 0,
      pending_hours: 0,
      minimum_teaching_workload: 0,
      teaching_workload: 0,
      makeup_teaching_workload: 0,
      total_teaching_workload: 0,
      excess_hours: 0,
      eligible_makeup_teaching_workload: 0,
      calculation_total_teaching_workload: 0,
      calculation_excess_hours: 0,
      meets_minimum: false,
      eligible_for_disbursement: false,
      regular_amount: 0,
      international_amount: 0,
      total_amount: 0,
      regular_summary: null,
      international_summary: null,
      regular_line_items: [],
      international_line_items: [],
      regular_weekly_workload: this.emptyRegularWeeklyWorkload(),
      international_weekly_workload: this.emptyInternationalWeeklyWorkload(),
      academic_term: null,
      erp_comparison: {
        has_result: false,
        system_request_count: 0,
        system_matched_request_count: 0,
        system_unmatched_request_count: 0,
        import_unmatched_count: 0,
        imported_at: null,
        result_id: null,
      },
    };
  }

  private normalizeRegularWeeklyWorkload(
    workload: CompensationSummaryRegularWeeklyWorkload | null | undefined,
  ): CompensationSummaryRegularWeeklyWorkload {
    return {
      course_items: (workload?.course_items ?? []).map((item) =>
        this.normalizeRegularWeeklyWorkloadItem(item)),
      undergraduate_project_course_items: (
        workload?.undergraduate_project_course_items ?? []
      ).map((item) => this.normalizeRegularWeeklyWorkloadItem(item)),
      undergraduate_project_research_primary_advisor_student_count: this.normalizeNumber(
        workload?.undergraduate_project_research_primary_advisor_student_count,
      ),
      undergraduate_project_research_co_advisor_student_count: this.normalizeNumber(
        workload?.undergraduate_project_research_co_advisor_student_count,
      ),
      undergraduate_project_research_student_count: this.normalizeNumber(
        workload?.undergraduate_project_research_student_count,
      ),
      undergraduate_project_research_advisor_weekly_hours: this.normalizeNumber(
        workload?.undergraduate_project_research_advisor_weekly_hours,
      ),
      project_research_primary_advisor_student_count: this.normalizeNumber(
        workload?.project_research_primary_advisor_student_count,
      ),
      project_research_co_advisor_student_count: this.normalizeNumber(
        workload?.project_research_co_advisor_student_count,
      ),
      project_research_student_count: this.normalizeNumber(workload?.project_research_student_count),
      project_research_weekly_hours: this.normalizeNumber(workload?.project_research_weekly_hours),
      administration_position_code: workload?.administration_position_code ?? null,
      administration_position_label: workload?.administration_position_label ?? null,
      administration_weekly_hours: this.normalizeNumber(workload?.administration_weekly_hours),
      total_weekly_hours: this.normalizeNumber(workload?.total_weekly_hours),
    };
  }

  private emptyRegularWeeklyWorkload(): CompensationSummaryRegularWeeklyWorkload {
    return {
      course_items: [],
      undergraduate_project_course_items: [],
      undergraduate_project_research_primary_advisor_student_count: 0,
      undergraduate_project_research_co_advisor_student_count: 0,
      undergraduate_project_research_student_count: 0,
      undergraduate_project_research_advisor_weekly_hours: 0,
      project_research_primary_advisor_student_count: 0,
      project_research_co_advisor_student_count: 0,
      project_research_student_count: 0,
      project_research_weekly_hours: 0,
      administration_position_code: null,
      administration_position_label: null,
      administration_weekly_hours: 0,
      total_weekly_hours: 0,
    };
  }

  private normalizeInternationalWeeklyWorkload(
    workload: CompensationSummaryInternationalWeeklyWorkload | null | undefined,
  ): CompensationSummaryInternationalWeeklyWorkload {
    return {
      minimum_weekly_hours: this.normalizeNumber(workload?.minimum_weekly_hours),
      regular_weekly_hours: this.normalizeNumber(workload?.regular_weekly_hours),
      workload_shortfall_hours: this.normalizeNumber(workload?.workload_shortfall_hours),
      course_items: (workload?.course_items ?? []).map((item) =>
        this.normalizeInternationalWeeklyWorkloadItem(item)),
      undergraduate_project_course_items: (
        workload?.undergraduate_project_course_items ?? []
      ).map((item) => this.normalizeInternationalWeeklyWorkloadItem(item)),
      total_weekly_hours: this.normalizeNumber(workload?.total_weekly_hours),
      total_offset_weekly_hours: this.normalizeNumber(workload?.total_offset_weekly_hours),
      total_payable_weekly_hours: this.normalizeNumber(workload?.total_payable_weekly_hours),
      payable_percentage: this.normalizeNumber(workload?.payable_percentage),
    };
  }

  private emptyInternationalWeeklyWorkload(): CompensationSummaryInternationalWeeklyWorkload {
    return {
      minimum_weekly_hours: 18,
      regular_weekly_hours: 0,
      workload_shortfall_hours: 18,
      course_items: [],
      undergraduate_project_course_items: [],
      total_weekly_hours: 0,
      total_offset_weekly_hours: 0,
      total_payable_weekly_hours: 0,
      payable_percentage: 0,
    };
  }

  private normalizeRegularWeeklyWorkloadItem(
    item: CompensationSummaryRegularWeeklyWorkloadItem,
  ): CompensationSummaryRegularWeeklyWorkloadItem {
    return {
      ...item,
      assigned_teaching_hours: this.normalizeNumber(item.assigned_teaching_hours),
      group_teaching_hours: this.normalizeNumber(item.group_teaching_hours),
      workload_share_percentage: this.normalizeNumber(item.workload_share_percentage),
      lecture_credits: item.lecture_credits == null ? null : this.normalizeNumber(item.lecture_credits),
      laboratory_credits: item.laboratory_credits == null
        ? null
        : this.normalizeNumber(item.laboratory_credits),
      base_weekly_hours: item.base_weekly_hours == null
        ? null
        : this.normalizeNumber(item.base_weekly_hours),
      weekly_hours: item.weekly_hours == null ? null : this.normalizeNumber(item.weekly_hours),
    };
  }

  private normalizeInternationalWeeklyWorkloadItem(
    item: CompensationSummaryInternationalWeeklyWorkloadItem,
  ): CompensationSummaryInternationalWeeklyWorkloadItem {
    return {
      ...this.normalizeRegularWeeklyWorkloadItem(item),
      offset_weekly_hours: item.offset_weekly_hours == null
        ? null
        : this.normalizeNumber(item.offset_weekly_hours),
      payable_weekly_hours: item.payable_weekly_hours == null
        ? null
        : this.normalizeNumber(item.payable_weekly_hours),
      payable_percentage: item.payable_percentage == null
        ? null
        : this.normalizeNumber(item.payable_percentage),
    };
  }
}
